import Button from 'features/common/components/Button'
import Checkbox from 'features/common/components/Checkbox'
import StepProgressBar from 'features/common/components/StepProgressBar'
import {Country} from 'features/common/core/entitites'
import {useTypedTranslation} from 'i18n'
import {useTranslation} from 'react-i18next'
import FormField from './common/components/FormField'
import FormInput from './common/components/FormInput'
import FormStageContainer from './common/components/FormStageContainer'
import PasswordFormInput from './common/components/PasswordFormInput'
import useSignup from '../../common/hooks/useSignup'
import arrowBottom from 'assets/arrow-bottom-wide.svg'

const countryOptions = [
    {value: 'ro', label: 'Romania'},
    {value: 'bg', label: 'Bulgaria'},
] as const

const Signup = ({onCancel}: {onCancel: () => void}) => {
    const {t} = useTranslation()
    const {t: typedT} = useTypedTranslation()
    const {data, errors, onChange, onSubmit, onSubmitError, loading} =
        useSignup()
    const onChangeCountry = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const v = e.target.value
        onChange({prop: 'country', value: v.toString() as Country})
    }
    const onChangeInput =
        (prop: keyof typeof data) =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange({prop, value: e.target.value.toString()})
        }

    const onChangeTOS = () => {
        onChange({prop: 'tos', value: !data.tos})
    }

    const noErrors = Object.keys(errors).length === 0
    const noEmptyFields = !!(
        data.name &&
        data.organization &&
        data.country &&
        data.phone &&
        data.email &&
        data.password &&
        data.tos
    )

    const disableSubmit = !(noErrors && noEmptyFields)
    return (
        <FormStageContainer title="Sign up">
            <form
                className="flex w-[18.5rem] flex-col items-stretch gap-6 xs:w-[21rem] xs:gap-12"
                onSubmit={(e) => e.preventDefault()}
            >
                <div className="flex min-w-0 flex-col items-stretch gap-6 text-sm">
                    <FormField title={t('AUTH_NAME')}>
                        <FormInput
                            value={data.name}
                            onChange={onChangeInput('name')}
                            error={errors.name}
                            placeholder={t('AUTH_NAME_PLACEHOLDER')}
                            name="name"
                            autoComplete="name"
                        />
                    </FormField>
                    <FormField title={t('AUTH_ORGANIZATION')}>
                        <FormInput
                            value={data.organization}
                            onChange={onChangeInput('organization')}
                            error={errors.organization}
                            placeholder={t('AUTH_ORGANIZATION_PLACEHOLDER')}
                            type="text"
                            name="organization"
                            autoComplete="organization"
                        />
                    </FormField>
                    <FormField title={t('AUTH_COUNTRY')}>
                        <div className="relative w-full">
                            <select
                                value={data.country}
                                className={`h-9 w-full appearance-none rounded-full border-2 border-grey-70 bg-white px-5 ${
                                    data.country === ''
                                        ? 'text-grey-20'
                                        : 'text-black'
                                } min-w-0 text-sm active:border-black`}
                                placeholder={t(
                                    'AUTH_COUNTRY_PLACEHOLDER_SHORT',
                                )}
                                onChange={onChangeCountry}
                            >
                                <option value="" disabled>
                                    Select your option
                                </option>
                                {countryOptions.map((co) => (
                                    <option key={co.value} value={co.value}>
                                        {co.label}
                                    </option>
                                ))}
                            </select>
                            <img
                                src={arrowBottom}
                                className="absolute top-1/2 right-3 -translate-y-1/2"
                            />
                        </div>
                    </FormField>
                    <FormField title={t('AUTH_PHONE')}>
                        <FormInput
                            value={data.phone}
                            onChange={onChangeInput('phone')}
                            error={errors.phone}
                            placeholder={t('AUTH_PHONE_PLACEHOLDER')}
                            type="tel"
                            pattern="\+?\d+"
                            inputMode="tel"
                            name="tel"
                            autoComplete="tel"
                        />
                    </FormField>
                    <FormField title={t('AUTH_EMAIL')}>
                        <FormInput
                            value={data.email}
                            onChange={onChangeInput('email')}
                            error={errors.email}
                            placeholder={t('AUTH_EMAIL_PLACEHOLDER')}
                            type="email"
                            name="email"
                            autoComplete="email"
                        />
                    </FormField>
                    <FormField title={t('AUTH_PASSWORD')}>
                        <PasswordFormInput
                            value={data.password}
                            onChange={onChangeInput('password')}
                            error={errors.password}
                            placeholder={t('AUTH_SIGNUP_PASSWORD_PLACEHOLDER')}
                            type="password"
                            name="current-password"
                            autoComplete="current-password"
                        />
                    </FormField>
                    <div className="flex items-center gap-5">
                        <div>
                            <Checkbox value={data.tos} onChange={onChangeTOS} />
                        </div>
                        {t('AUTH_STRIPE_TOS')}
                    </div>
                </div>
                {onSubmitError && (
                    <div className="pl-4 text-xs text-red-700">
                        {typedT(onSubmitError.message)}
                    </div>
                )}
                <div className="flex justify-between gap-1">
                    <Button
                        onClick={onCancel}
                        view="alt"
                        className="h-9 rounded-full text-sm"
                        type="reset"
                    >
                        {t('AUTH_CANCEL_BUTTON')}
                    </Button>
                    <Button
                        disabled={disableSubmit}
                        className="h-9 rounded-full text-sm"
                        onClick={onSubmit}
                        isLoading={loading}
                        type="submit"
                    >
                        {t('AUTH_SIGNUP_BUTTON')}
                    </Button>
                </div>
                <StepProgressBar
                    points={[
                        {
                            title: t('ONBOARDING_STEP_REGISTRATION'),
                            status: 'current',
                        },
                        {title: t('ONBOARDING_STEP_EMAIL'), status: 'empty'},
                        {title: t('ONBOARDING_STEP_STRIPE'), status: 'empty'},
                    ]}
                />
            </form>
        </FormStageContainer>
    )
}

export default Signup
